import * as React from 'react';
import * as Sentry from '@sentry/react';
import { createRoot } from 'react-dom/client';
import type { ErrorEvent } from '@sentry/core';
import { QueryClientProvider } from '@tanstack/react-query';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { ThemeProvider as StyledComponentThemeProvider, DefaultTheme } from 'styled-components';
import { queryClient } from './core/reactQueryCore';
import App from './appComponents/App';
import AppVersionGate from './commonComponents/AppVersionGate';
import '@progress/kendo-theme-default/dist/all.css';
import config from './config.general';
import SentryErrorBoundary from './appComponents/SentryErrorBoundary';
import { getAppVersion } from './lib/utils';
import { personalTheme, appTheme } from './appComponents/themes/brandingTheme';

const isIgnorableError = (event: ErrorEvent) => {
	const exception = event?.exception?.values?.[0];
	const errorValue = exception?.value;

	// When this error occurred which caused by the browser's extension, stop reporting to sentry.
	if (errorValue === 'ResizeObserver loop limit exceeded') return true;

	// Ignore AbortError from fetch requests cancelled during navigation
	if (errorValue?.includes('The operation was aborted')) return true;

	// Only ignore specific known harmless UnhandledRejection patterns, not all of them
	if (errorValue?.includes('UnhandledRejection')) {
		// Ignore chunk loading errors (network/deployment issues)
		if (errorValue.includes('ChunkLoadError')) return true;
		// Ignore specific harmless Promise rejections
		if (errorValue.includes('Network request failed')) return true;
	}

	// Ignore SyntaxError from split chunks failing to parse on outdated WebViews
	// (notably Google Play's pre-launch review bots on WebView 91). The chunk loads
	// but the V8 in that WebView can't parse modern JS syntax, surfacing as
	// "Unexpected token" via the global onerror handler. Real users aren't on these
	// WebViews; until we add a browserslist/transpile target this is just noise.
	if (
		exception?.type === 'SyntaxError' &&
		exception?.stacktrace?.frames?.some((f) => /^https:\/\/localhost\/.*\.bundle\.js/.test(f?.abs_path ?? ''))
	) {
		return true;
	}

	// Ignore uncaught axios timeouts from Stream SDK (chat/feed) background requests
	// such as queryChannels, token refresh, and connection health checks. These fire
	// on flaky mobile networks and the SDK retries internally — they are not
	// user-facing. We only skip when mechanism.handled is false (i.e. the rejection
	// reached a global handler uncaught) so that timeouts on our own axios calls —
	// which are wrapped in react-query and caught — still report to Sentry if they
	// ever do surface.
	if (
		exception?.type === 'AxiosError' &&
		errorValue?.startsWith('timeout of ') &&
		exception?.mechanism?.handled === false
	) {
		return true;
	}

	const bingbotBrowserError = 't.at is not a function'; // https://kellypartners.sentry.io/issues/5148782751
	if (errorValue === bingbotBrowserError) return true;

	return false;
};

const initializeApp = async () => {
	if (config.sentryDsn) {
		// Get the actual app version (native or web)
		const appVersion = await getAppVersion();

		Sentry.init({
			environment: config.environment,
			release: `${config.appName}@${appVersion}`,
			dsn: config.sentryDsn,
			beforeSend: (event) => (isIgnorableError(event) ? null : event)
		});
	}

	const container = document.getElementById('app');
	const root = createRoot(container!);
	root.render(
		<SentryErrorBoundary>
			<QueryClientProvider client={queryClient}>
				<MuiThemeProvider theme={createTheme()}>
					<StyledComponentThemeProvider theme={{ ...personalTheme, ...appTheme } as DefaultTheme}>
						<AppVersionGate>
							<App />
						</AppVersionGate>
					</StyledComponentThemeProvider>
				</MuiThemeProvider>
			</QueryClientProvider>
		</SentryErrorBoundary>
	);
};

initializeApp();
